import { useEffect, useState } from "react";
import { Users, ShieldCheck, Trash2, KeyRound, Pencil, Loader2, Check, X, BarChart2, Bell, LayoutDashboard, RefreshCw, UserPlus } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Admin() {
  const { user: me } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);   // user being edited
  const [pwUser, setPwUser] = useState(null);       // user for password reset
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.adminStats(), api.adminListUsers()]);
      setStats(s);
      setUsers(u);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Kein Admin-Zugriff.");
      } else {
        toast.error("Daten konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (user, field) => {
    const newVal = !user[field];
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, [field]: newVal } : u));
    try {
      await api.adminUpdateUser(user.id, { [field]: newVal });
      toast.success(`${field === "is_active" ? (newVal ? "Aktiviert" : "Deaktiviert") : (newVal ? "Admin-Rechte erteilt" : "Admin-Rechte entzogen")}.`);
    } catch {
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, [field]: !newVal } : u));
      toast.error("Änderung fehlgeschlagen.");
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updated = await api.adminUpdateUser(editUser.id, {
        name: editUser.name,
        email: editUser.email,
      });
      setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
      setEditUser(null);
      toast.success("Benutzer aktualisiert.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const handleResetPw = async () => {
    if (newPw.length < 6) { toast.error("Mindestens 6 Zeichen."); return; }
    setSaving(true);
    try {
      await api.adminResetPassword(pwUser.id, newPw);
      setPwUser(null); setNewPw("");
      toast.success("Passwort zurückgesetzt.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler.");
    } finally { setSaving(false); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Benutzer "${user.name}" (${user.email}) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try {
      await api.adminDeleteUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
      toast.success("Benutzer gelöscht.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || newUser.password.length < 6) { toast.error("E-Mail und Passwort (min. 6 Zeichen) erforderlich."); return; }
    setSaving(true);
    try {
      const created = await api.adminCreateUser(newUser);
      setUsers(prev => [...prev, created]);
      setCreateOpen(false);
      setNewUser({ email: "", name: "", password: "" });
      toast.success(`Benutzer ${created.email} angelegt.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen.");
    } finally { setSaving(false); }
  };

  if (!me?.is_admin) {
    return (
      <div className="p-12 flex flex-col items-center gap-4 text-center">
        <ShieldCheck size={40} className="text-zinc-600" />
        <div className="font-display font-bold text-lg">Kein Zugriff</div>
        <p className="text-muted-foreground text-sm">Diese Seite ist nur für Administratoren.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-full" data-testid="admin-page">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1">Administration</div>
          <h1 className="font-display font-black text-3xl tracking-tight">Plattform-Verwaltung</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateOpen(true)}
            className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold uppercase tracking-wider text-xs h-8">
            <UserPlus size={12} className="mr-1" /> Benutzer anlegen
          </Button>
          <Button onClick={load} variant="outline" disabled={loading}
            className="border-border bg-transparent hover:bg-muted text-xs h-8">
            <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Users,         label: "Nutzer gesamt",   value: stats.total_users },
            { icon: Check,         label: "Aktive Nutzer",   value: stats.active_users },
            { icon: LayoutDashboard, label: "Setups",        value: stats.total_setups },
            { icon: Bell,          label: "Alerts gesamt",   value: stats.total_alerts },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="border border-border bg-card p-4 space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon size={13} />
                <span className="text-[10px] uppercase tracking-widest font-display">{label}</span>
              </div>
              <div className="font-mono-num font-black text-2xl">{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Users table */}
      <div className="border border-border">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Users size={14} className="text-muted-foreground" />
          <div className="font-display font-bold text-sm uppercase tracking-wider">Registrierte Nutzer</div>
          <span className="ml-auto text-xs text-muted-foreground">{users.length} Nutzer</span>
        </div>

        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" size={24} />
          </div>
        ) : (
          <>
            {/* Desktop table — hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {["Name", "E-Mail", "Registriert", "Aktiv", "Admin", "E-Mail", "Aktionen"].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-display font-bold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                      <td className="px-3 py-2.5 font-medium text-sm">
                        {user.name}
                        {user.id === me.id && <span className="ml-1.5 text-[9px] bg-zinc-700 text-zinc-300 px-1.5 py-0.5 font-display uppercase">Du</span>}
                      </td>
                      <td className="px-3 py-2.5 font-mono-num text-xs text-muted-foreground">{user.email}</td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString("de-DE") : "—"}
                      </td>
                      <td className="px-3 py-2.5"><Switch checked={!!user.is_active} onCheckedChange={() => handleToggle(user, "is_active")} disabled={user.id === me.id} /></td>
                      <td className="px-3 py-2.5"><Switch checked={!!user.is_admin} onCheckedChange={() => handleToggle(user, "is_admin")} disabled={user.id === me.id} /></td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-display font-bold uppercase px-1.5 py-0.5 ${user.email_alerts_enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-500"}`}>
                          {user.email_alerts_enabled ? "An" : "Aus"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex gap-1">
                          <button onClick={() => setEditUser({ ...user })} title="Bearbeiten" className="p-1.5 border border-border hover:bg-muted rounded-sm transition-colors"><Pencil size={11} /></button>
                          <button onClick={() => { setPwUser(user); setNewPw(""); }} title="Passwort" className="p-1.5 border border-border hover:bg-muted rounded-sm transition-colors"><KeyRound size={11} /></button>
                          {user.id !== me.id && (
                            <button onClick={() => handleDelete(user)} title="Löschen" className="p-1.5 border border-rose-900/50 hover:bg-rose-950/40 text-rose-500 rounded-sm transition-colors"><Trash2 size={11} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — visible only on mobile */}
            <div className="md:hidden divide-y divide-border">
              {users.map(user => (
                <div key={user.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        {user.name}
                        {user.id === me.id && <span className="text-[9px] bg-zinc-700 text-zinc-300 px-1.5 py-0.5 font-display uppercase">Du</span>}
                        {user.is_admin && <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 font-display uppercase">Admin</span>}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono-num mt-0.5">{user.email}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString("de-DE") : ""}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => setEditUser({ ...user })} className="p-2 border border-border hover:bg-muted rounded-sm"><Pencil size={13} /></button>
                      <button onClick={() => { setPwUser(user); setNewPw(""); }} className="p-2 border border-border hover:bg-muted rounded-sm"><KeyRound size={13} /></button>
                      {user.id !== me.id && (
                        <button onClick={() => handleDelete(user)} className="p-2 border border-rose-900/50 text-rose-500 hover:bg-rose-950/40 rounded-sm"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <label className="flex items-center gap-2">
                      <Switch checked={!!user.is_active} onCheckedChange={() => handleToggle(user, "is_active")} disabled={user.id === me.id} />
                      Aktiv
                    </label>
                    <label className="flex items-center gap-2">
                      <Switch checked={!!user.is_admin} onCheckedChange={() => handleToggle(user, "is_admin")} disabled={user.id === me.id} />
                      Admin
                    </label>
                    <span className={`self-center text-[10px] font-display font-bold uppercase px-1.5 py-0.5 ${user.email_alerts_enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-500"}`}>
                      E-Mail {user.email_alerts_enabled ? "An" : "Aus"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Edit modal */}
      {editUser && (
        <Modal title="Benutzer bearbeiten" onClose={() => setEditUser(null)}>
          <div className="space-y-3">
            <Field label="Name">
              <Input value={editUser.name} onChange={e => setEditUser(u => ({ ...u, name: e.target.value }))}
                className="bg-background border-border" />
            </Field>
            <Field label="E-Mail">
              <Input value={editUser.email} onChange={e => setEditUser(u => ({ ...u, email: e.target.value }))}
                className="bg-background border-border font-mono-num" />
            </Field>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveEdit} disabled={saving}
                className="bg-foreground text-background hover:opacity-90 font-display font-bold uppercase tracking-wider text-xs">
                {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <Check size={13} className="mr-1" />}
                Speichern
              </Button>
              <Button variant="outline" onClick={() => setEditUser(null)}
                className="border-border bg-transparent hover:bg-muted text-xs">
                Abbrechen
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Password reset modal */}
      {pwUser && (
        <Modal title={`Passwort zurücksetzen — ${pwUser.name}`} onClose={() => setPwUser(null)}>
          <div className="space-y-3">
            <Field label="Neues Passwort">
              <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                placeholder="Mindestens 6 Zeichen" className="bg-background border-border" />
            </Field>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleResetPw} disabled={saving || newPw.length < 6}
                className="bg-foreground text-background hover:opacity-90 font-display font-bold uppercase tracking-wider text-xs">
                {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <KeyRound size={13} className="mr-1" />}
                Zurücksetzen
              </Button>
              <Button variant="outline" onClick={() => setPwUser(null)}
                className="border-border bg-transparent hover:bg-muted text-xs">
                Abbrechen
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create user modal */}
      {createOpen && (
        <Modal title="Neuen Benutzer anlegen" onClose={() => setCreateOpen(false)}>
          <div className="space-y-3">
            <Field label="E-Mail">
              <Input type="email" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))}
                placeholder="kunde@beispiel.de" className="bg-background border-border font-mono-num" />
            </Field>
            <Field label="Name (optional)">
              <Input value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))}
                placeholder="Max Mustermann" className="bg-background border-border" />
            </Field>
            <Field label="Temporäres Passwort">
              <Input type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))}
                placeholder="Mindestens 6 Zeichen" className="bg-background border-border" />
            </Field>
            <p className="text-[10px] text-zinc-500">Der Benutzer wird beim ersten Login aufgefordert, das Passwort zu ändern.</p>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreateUser} disabled={saving || !newUser.email || newUser.password.length < 6}
                className="bg-foreground text-background hover:opacity-90 font-display font-bold uppercase tracking-wider text-xs">
                {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <UserPlus size={13} className="mr-1" />}
                Anlegen
              </Button>
              <Button variant="outline" onClick={() => setCreateOpen(false)}
                className="border-border bg-transparent hover:bg-muted text-xs">
                Abbrechen
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-sm uppercase tracking-wider">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm transition-colors">
            <X size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display">{label}</div>
      {children}
    </div>
  );
}
